import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

interface OrderItem {
  menuItemId: string;
  quantity: number;
  size: 'small' | 'medium' | 'large';
  sweetness: '0' | '25' | '50' | '70' | '100';
  modifiers?: {
    modifierGroupId: string;
    modifierOptionId: string;
    name: string;
    priceAdjustment: number;
  }[];
  notes?: string;
}

interface CreateOrderInput {
  branchId: string;
  customerId?: string;
  items: OrderItem[];
  loyaltyPointsToRedeem?: number;
  notes?: string;
}

interface Order {
  _id: string;
  orderNumber: string;
  branchId: string;
  customerId?: string;
  items: any[];
  subtotal: number;
  discounts: number;
  tax: number;
  total: number;
  payments: any[];
  status: string;
  isPaidInFull: boolean;
  loyaltyPointsRedeemed: number;
  loyaltyPointsEarned?: number;
  createdAt: string;
}

interface PaymentInput {
  method: 'cash' | 'card' | 'qr' | 'split';
  amount: number;
  transactionId?: string;
  cardLast4?: string;
  qrReference?: string;
}

// Create order mutation
export function useCreateOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateOrderInput) => {
      const response = await apiClient.post<APIResponse<{ order: Order }>>(
        '/orders',
        input
      );
      return response.data.data.order;
    },
    onSuccess: () => {
      // Invalidate orders and inventory queries
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

// Add payment to order mutation (legacy - requires orderId upfront)
export function useAddPayment(orderId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payment: PaymentInput) => {
      const response = await apiClient.post<APIResponse<{ order: Order }>>(
        `/orders/${orderId}/payment`,
        payment
      );
      return response.data.data.order;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
    },
  });
}

// Add payment mutation - accepts orderId dynamically
export function useAddPaymentDynamic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderId, payment }: { orderId: string; payment: PaymentInput }) => {
      const response = await apiClient.post<APIResponse<{ order: Order }>>(
        `/orders/${orderId}/payment`,
        payment
      );
      return response.data.data.order;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order', variables.orderId] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export type { CreateOrderInput, OrderItem, Order, PaymentInput };
